// src/components/inventory/InventoryReportTab.tsx
// Quanto prodotto se n'è andato in servizio e quanto è stato venduto:
// tre numeri in alto e il dettaglio per prodotto.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scissors, ShoppingBag, ArrowLeftRight } from 'lucide-react';

import { useInventory } from '@/contexts/InventoryContext';

type Period = 'month' | 'all';

const InventoryReportTab: React.FC = () => {
    const { t } = useTranslation();
    const { movements, products, getProductById } = useInventory();
    const [period, setPeriod] = useState<Period>('month');

    const inPeriod = useMemo(() => {
        if (period === 'all') return movements;
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return movements.filter((m) => new Date(m.created_at) >= start);
    }, [movements, period]);

    const stats = useMemo(() => {
        let usedQty = 0;
        let usedValue = 0;
        let soldQty = 0;
        let soldValue = 0;

        inPeriod.forEach((m) => {
            if (m.type === 'use') {
                usedQty += m.quantity;
                const price = Number(getProductById(m.product_id)?.price ?? 0);
                usedValue += price * m.quantity;
            } else if (m.type === 'sale') {
                soldQty += m.quantity;
                soldValue += Number(m.total_amount ?? 0);
            }
        });

        return { usedQty, usedValue, soldQty, soldValue, count: inPeriod.length };
    }, [inPeriod, getProductById]);

    const perProduct = useMemo(() => {
        const map = new Map<number, { used: number; sold: number }>();
        inPeriod.forEach((m) => {
            if (m.type === 'in') return;
            const row = map.get(m.product_id) || { used: 0, sold: 0 };
            if (m.type === 'use') row.used += m.quantity;
            else row.sold += m.quantity;
            map.set(m.product_id, row);
        });
        return Array.from(map.entries())
            .map(([productId, row]) => ({
                product: products.find((p) => p.id === productId),
                ...row,
            }))
            .sort((a, b) => b.sold + b.used - (a.sold + a.used));
    }, [inPeriod, products]);

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-2xl p-1.5 border border-gray-100 shadow-sm inline-flex gap-1">
                {([
                    { id: 'month' as Period, label: t('inventory.report.this_month', 'Questo mese') },
                    { id: 'all' as Period, label: t('inventory.report.all', 'Tutto') },
                ]).map((p) => (
                    <button
                        key={p.id}
                        type="button"
                        onClick={() => setPeriod(p.id)}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${period === p.id
                            ? 'bg-clip-500 text-white shadow-sm'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center mb-3">
                        <Scissors size={18} />
                    </div>
                    <p className="text-sm text-gray-500">{t('inventory.used_in_service', 'Usato in servizio')}</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">
                        {stats.usedQty} {t('inventory.pieces_short', 'pz')}
                    </p>
                    <p className="text-xs text-gray-400">
                        {t('inventory.report.value', 'Valore')} €{stats.usedValue.toFixed(2)}
                    </p>
                </div>

                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center mb-3">
                        <ShoppingBag size={18} />
                    </div>
                    <p className="text-sm text-gray-500">{t('inventory.sold', 'Venduto (rivendita)')}</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">
                        {stats.soldQty} {t('inventory.pieces_short', 'pz')}
                    </p>
                    <p className="text-xs text-gray-400">
                        {t('inventory.report.revenue', 'Ricavo')} €{stats.soldValue.toFixed(2)}
                    </p>
                </div>

                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center mb-3">
                        <ArrowLeftRight size={18} />
                    </div>
                    <p className="text-sm text-gray-500">{t('inventory.report.movements', 'Movimenti')}</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{stats.count}</p>
                    <p className="text-xs text-gray-400">
                        {period === 'month'
                            ? t('inventory.report.this_month_lower', 'questo mese')
                            : t('inventory.report.all_lower', 'in totale')}
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-5 py-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    <span>{t('inventory.report.product', 'Prodotto')}</span>
                    <span className="text-right w-16">{t('inventory.report.used', 'Usato')}</span>
                    <span className="text-right w-16">{t('inventory.report.soldcol', 'Venduto')}</span>
                    <span className="text-right w-20">{t('inventory.stock', 'Giacenza')}</span>
                </div>

                {perProduct.length === 0 ? (
                    <p className="p-8 text-center text-sm text-gray-400">
                        {t('inventory.report.empty', 'Nessun consumo né vendita nel periodo.')}
                    </p>
                ) : (
                    perProduct.map((row, index) => (
                        <div
                            key={row.product?.id ?? index}
                            className={`grid grid-cols-[1fr_auto_auto_auto] gap-4 px-5 py-3.5 items-center ${index > 0 ? 'border-t border-gray-100' : ''
                                }`}
                        >
                            <div className="min-w-0">
                                <p className="font-bold text-gray-900 truncate">
                                    {row.product?.name || t('inventory.history.deleted_product', 'Prodotto rimosso')}
                                </p>
                                <p className="text-xs text-gray-400">{row.product?.category}</p>
                            </div>
                            <span className="text-right w-16 text-sm font-bold tabular-nums text-clip-600">
                                {row.used || <span className="text-gray-300">—</span>}
                            </span>
                            <span className="text-right w-16 text-sm font-bold tabular-nums text-gray-900">
                                {row.sold || <span className="text-gray-300">—</span>}
                            </span>
                            <span className="text-right w-20 text-sm text-gray-500 tabular-nums">
                                {row.product ? `${row.product.stock} ${t('inventory.pieces_short', 'pz')}` : '—'}
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default InventoryReportTab;
